/* eslint-disable babel/camelcase */
import { ShareAlbum, ShareAlbumVariables } from 'd2/queries'
import { d } from 'd2/utils'
import {
  difference,
  isNil,
  some,
} from 'lodash-es'
import { gql } from '@apollo/client'
import { joinList } from 'd2/utils/intl'
import { prettyTrackDuration } from 'd2/utils/StringHelpers'
import { useNavigate } from 'd2/hooks/useRouter'
import { useQuerySwitch } from 'd2/hooks/useQuerySwitch'
import type { DocumentNode } from 'd2/types'
import type { PlaylistDataRow } from 'd2/components/AudioPlayer/Playlist/types'
import type { ReducerArg } from 'd2/hooks/useQuerySwitch'

const shareAlbumQuery: DocumentNode = gql`
  query ShareAlbum($secret: String!, $featuredRole: [String!], $performerRole: [String!]) {
    shared_album(secret: $secret) {
      id
      is_distributed
      partners {
        id
        name
        partner_url
        partner_eid
        hide_release_link
        type
        network {
          id
          name
        }
      }
      file_upload {
        id
        album_artwork_url
      }
      artist {
        id
        name
      }
      album_medias_cursor {
        count
        album_medias {
          id
          track_number
          media {
            id
            original_url
            artist {
              id
              name
            }
            file_upload {
              id
              duration_in_ms
              created_at
            }
            metadata {
              id
              song_name
              explicit_type
              copyright_name
              copyright_year
              genre
              label
              lyrics
              audio_isrc
              collaborating_artists {
                id
                name
                role
              }
              featuredArtists: collaborating_artists(roles: $featuredRole) {
                id
                name
              }
            }
          }
        }
      }
      metadata {
        id
        name
        genre
        label
        copyright_name
        copyright_year
        territories
        upc
        release_date
        collaborating_artists {
          id
          name
          role
        }
        additionalMainArtists: collaborating_artists(roles: $performerRole) {
          id
          name
        }
      }
    }
  }
`

const useShareAlbumQueryReducer = ({ data }: ReducerArg<ShareAlbum, ShareAlbumVariables>) => {
  const navigate = useNavigate()
  if (!data) return

  const {
    shared_album: album,
  } = data

  if (!album) {
    navigate('/d2/404')
    return
  }

  const {
    album_medias_cursor: {
      album_medias: albumMedias,
    },
    artist,
    file_upload: fileUpload,
    metadata,
    partners,
  } = album

  const {
    name: artistName,
  } = d(artist)

  const {
    album_artwork_url: albumArtworkUrl,
  } = d(fileUpload)

  const {
    additionalMainArtists,
    collaborating_artists: collaboratingArtists,
    copyright_name: copyrightName,
    copyright_year: copyrightYear,
    genre,
    label,
    name: albumName,
    release_date: releaseDate,
    territories,
    upc,
  } = metadata

  return {
    additionalMainArtists,
    albumArtworkUrl,
    albumName,
    artistName,
    collaboratingArtists,
    copyrightName,
    copyrightYear,
    genre,
    label,
    partners: partners.filter(({ hide_release_link, type }) => !hide_release_link && type === 'stream'),
    playlistData: albumMedias.map(({ media, track_number: track }): PlaylistDataRow => {
      const { artist: mediaArtist, file_upload: mediaFileUpload, metadata: mediaMetadata, original_url: originalUrl } = media
      const { name: mediaArtistName } = d(mediaArtist)
      const {
        audio_isrc: audioIsrc,
        collaborating_artists: mediaCollaboratingArtists,
        copyright_name: mediaCopyrightName,
        copyright_year: mediaCopyrightYear,
        explicit_type: explicitType,
        featuredArtists,
        genre: mediaGenre,
        label: mediaLabel,
        lyrics,
        song_name: songName = '...',
      } = d(mediaMetadata)
      const { duration_in_ms: durationInMs } = d(mediaFileUpload)
      const duration: string = isNil(durationInMs) ? 'N/A' : prettyTrackDuration(durationInMs)
      const albumAdditionalMainArtistNames: string[] = additionalMainArtists.map(({ name }) => name)
      const featuredArtistNames: string[] = (featuredArtists ?? []).map(({ name }) => name)
      const featuredArtistNamesToUse: string[] = difference(featuredArtistNames, albumAdditionalMainArtistNames)
      const songNameWithFeaturedArtists: string = some(featuredArtistNamesToUse) ? `${songName} feat. ${joinList(featuredArtistNamesToUse)}` : songName

      return {
        artistName: mediaArtistName,
        audioIsrc,
        collaboratingArtists: mediaCollaboratingArtists,
        copyrightName: mediaCopyrightName,
        copyrightYear: mediaCopyrightYear,
        duration,
        explicitType,
        genre: mediaGenre,
        label: mediaLabel,
        lyrics,
        name: songNameWithFeaturedArtists,
        originalUrl,
        track,
      }
    }),
    releaseDate,
    territories,
    upc,
  }
}

export const useShareAlbumQuery = (secret: string) => useQuerySwitch<ShareAlbum, ShareAlbumVariables, typeof useShareAlbumQueryReducer>(shareAlbumQuery, {
  fetchPolicy: 'cache-and-network',
  reducer: useShareAlbumQueryReducer,
  variables: {
    // TODO: Refactor this file not rely on hard-coded string values for role names [VYD-13122]
    featuredRole: ['featuring'],
    performerRole: ['performer'],
    secret,
  },
})
